import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { LoginPage } from '@/features/auth/LoginPage';
import { Spinner } from '@/components/ui/misc';

// Chargement paresseux des pages (code-splitting) pour alléger le bundle initial.
const named = <T extends Record<string, unknown>>(loader: () => Promise<T>, key: keyof T) =>
  lazy(() => loader().then((m) => ({ default: m[key] as React.ComponentType })));

const DashboardPage = named(() => import('@/pages/DashboardPage'), 'DashboardPage');
const CustomersPage = named(() => import('@/features/customers/CustomersPage'), 'CustomersPage');
const CustomerDetailPage = named(() => import('@/features/customers/CustomerDetailPage'), 'CustomerDetailPage');
const VehiclesPage = named(() => import('@/features/vehicles/VehiclesPage'), 'VehiclesPage');
const VehicleDetailPage = named(() => import('@/features/vehicles/VehicleDetailPage'), 'VehicleDetailPage');
const AppointmentsPage = named(() => import('@/features/appointments/AppointmentsPage'), 'AppointmentsPage');
const RepairOrdersPage = named(() => import('@/features/repair-orders/RepairOrdersPage'), 'RepairOrdersPage');
const RepairOrderDetailPage = named(() => import('@/features/repair-orders/RepairOrderDetailPage'), 'RepairOrderDetailPage');
const PartsPage = named(() => import('@/features/parts/PartsPage'), 'PartsPage');
const SuppliersPage = named(() => import('@/features/suppliers/SuppliersPage'), 'SuppliersPage');
const SupplierDetailPage = named(() => import('@/features/suppliers/SupplierDetailPage'), 'SupplierDetailPage');
const PurchasesPage = named(() => import('@/features/purchases/PurchasesPage'), 'PurchasesPage');
const PurchaseDetailPage = named(() => import('@/features/purchases/PurchaseDetailPage'), 'PurchaseDetailPage');
const CaissePage = named(() => import('@/features/sales/CaissePage'), 'CaissePage');
const InvoicesPage = named(() => import('@/features/invoices/InvoicesPage'), 'InvoicesPage');
const InvoiceDetailPage = named(() => import('@/features/invoices/InvoiceDetailPage'), 'InvoiceDetailPage');
const EmployeesPage = named(() => import('@/features/employees/EmployeesPage'), 'EmployeesPage');
const EmployeeDetailPage = named(() => import('@/features/employees/EmployeeDetailPage'), 'EmployeeDetailPage');
const ReportsPage = named(() => import('@/features/reports/ReportsPage'), 'ReportsPage');
const UsersPage = named(() => import('@/features/users/UsersPage'), 'UsersPage');
const RolesPage = named(() => import('@/features/users/RolesPage'), 'RolesPage');
const MessagingPage = named(() => import('@/features/messaging/MessagingPage'), 'MessagingPage');
const SettingsPage = named(() => import('@/features/settings/SettingsPage'), 'SettingsPage');

function PageFallback() {
  return <div className="flex justify-center py-24"><Spinner /></div>;
}

export function AppRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route index element={<DashboardPage />} />

            <Route element={<ProtectedRoute permission="customers:read" />}>
              <Route path="clients" element={<CustomersPage />} />
              <Route path="clients/:id" element={<CustomerDetailPage />} />
            </Route>
            <Route element={<ProtectedRoute permission="vehicles:read" />}>
              <Route path="vehicules" element={<VehiclesPage />} />
              <Route path="vehicules/:id" element={<VehicleDetailPage />} />
            </Route>
            <Route element={<ProtectedRoute permission="appointments:read" />}>
              <Route path="rendez-vous" element={<AppointmentsPage />} />
            </Route>
            <Route element={<ProtectedRoute permission="repair_orders:read" />}>
              <Route path="reparations" element={<RepairOrdersPage />} />
              <Route path="reparations/:id" element={<RepairOrderDetailPage />} />
            </Route>
            <Route element={<ProtectedRoute permission="parts:read" />}>
              <Route path="pieces" element={<PartsPage />} />
            </Route>
            <Route element={<ProtectedRoute permission="suppliers:read" />}>
              <Route path="fournisseurs" element={<SuppliersPage />} />
              <Route path="fournisseurs/:id" element={<SupplierDetailPage />} />
            </Route>
            <Route element={<ProtectedRoute permission="purchases:read" />}>
              <Route path="achats" element={<PurchasesPage />} />
              <Route path="achats/:id" element={<PurchaseDetailPage />} />
            </Route>
            <Route element={<ProtectedRoute permission="sales:read" />}>
              <Route path="ventes" element={<CaissePage />} />
            </Route>
            <Route element={<ProtectedRoute permission="invoices:read" />}>
              <Route path="facturation" element={<InvoicesPage />} />
              <Route path="facturation/:id" element={<InvoiceDetailPage />} />
            </Route>
            <Route element={<ProtectedRoute permission="employees:read" />}>
              <Route path="employes" element={<EmployeesPage />} />
              <Route path="employes/:id" element={<EmployeeDetailPage />} />
            </Route>
            <Route element={<ProtectedRoute permission="reports:read" />}>
              <Route path="rapports" element={<ReportsPage />} />
            </Route>
            <Route element={<ProtectedRoute permission="users:read" />}>
              <Route path="utilisateurs" element={<UsersPage />} />
              <Route path="roles" element={<RolesPage />} />
            </Route>
            <Route element={<ProtectedRoute permission="settings:read" />}>
              <Route path="messagerie" element={<MessagingPage />} />
              <Route path="parametres" element={<SettingsPage />} />
            </Route>

            <Route path="*" element={<div className="py-24 text-center text-muted-foreground">Page introuvable</div>} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
